import React from "react";
import moment from "moment";

import { ScreeningTestDefinition } from "@/Common/Api/Screening/Dto/Responses/ScreeningTestDefinition";
import { ScreeningTestProfile } from "@/Common/Api/Screening/Dto/Responses/ScreeningTestProfile";
import { AttachDocumentLink } from "@/Common/Components/IconControls/AttachDocumentLink";
import { AttachedDocumentList } from "@/Common/Components/IconControls/AttachedDocumentList";
import { LockControl } from "@/Common/Components/IconControls/LockControl";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { ShorthandDateSetter } from "@/Common/Components/Inputs/ShorthandDateSetter";
import { SingleSelectTextBoxControl } from "@/Common/Components/Inputs/SingleSelectTextBoxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { TextboxControlWithSections } from "@/Common/Components/Inputs/TextboxControlWithSections";
import { Modal } from "@/Common/Components/Modals/Modal";
import { CliniciansHelper } from "@/Common/Helpers/CliniciansHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelSection, SearchableItemModelT, SearchableSection } from "@/Common/Models/SearchableItem";
import { ScreeningTestFrequencyOption, ScreeningTestReminderOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { IconButton } from "@/DesignSystem";
import { LinkButton } from "@/DesignSystem/Molecules/LinkButton";
import { Notice } from "@/DesignSystem/Molecules/Notice";
import { FileSelectionContainer } from "@/ExportableFile/Components/FileSelection/FileSelectionContainer";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { FollowUpItem } from "@/Patient/Models/MedicalSummary/FollowUpItem";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemScreeningTest, ScreeningTestDueDate, ScreeningTestInterpretation, ScreeningTestStatus } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { FollowUpResponse } from "@/Patient/Models/MedicalSummary/Response/FollowUpResponse";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { AddRequestCommentPopupComponent } from "@/Portfolio/Components/MedicalRequestForm/AddRequestCommentPopupComponent";
import { MedicalRequestPopupController } from "@/Portfolio/Components/MedicalRequestForm/MedicalRequestPopupController";
import { MedicalRequestStatus } from "@/Portfolio/Models/MedicalRequestStatus";
import { ScreeningTestWarningIcon } from "../ScreeningTest/ScreeningTestWarningIcon";
import { BaseMedicalItemsView, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";
import { FollowupSelectionComponent } from "./FollowupSelectionComponent";

import styles from "./MedicalItemsScreeningTest.module.scss";

import PencilSimpleLightIcon from "@/DesignSystem/Atoms/Icons/Types/PencilSimple/pencil-simple-light.svg";
import XIcon from "@/DesignSystem/Atoms/Icons/Types/X/x-bold.svg";
import img_icon_attachment_icon from "@/Globals/_images/icons/attachment_icon.png";

class MedicalItemsScreeningTestViewState extends BaseMedicalItemsViewState {
    // Screening test item states
    public id: string;
    public displayId: string;
    public selectedProfileId: number;
    public selectedTestId: number;
    public testName: string;
    public selectedFrequencyDropDownId: string;
    public selectedReminderDropDownId: string;
    public selectedInterpretationLookupId: string;
    public originalInterpretationLookupId: string;
    public screeningDueDate: ScreeningTestDueDate;
    public completionDate: Date;
    public completionDateText: string;
    public selectedStatusLookupId: string;
    public startAge: number;
    public endAge: number;
    public attachments: AttachedObjectResponse[];
    public followups: FollowUpResponse[];
    public notes: string;
    public requestingStaffContactFacetId: string;

    // UI state
    public showFileSelection: boolean;
    public currentFile: AttachedObjectResponse;
    public showFollowupSelection: boolean;
    public currentFollowUp: FollowUpResponse;
    public showAddCommentPopup: boolean;
    public commentsPopupClientX: number;
    public commentsPopupClientY: number;
    public isTestSelected: boolean;

    public dateFormat: string;

    public followupIdToRemove: string;

    public static getDefault(_model?: MedicalItemScreeningTestResponse, contactFacetId?: string): MedicalItemsScreeningTestViewState {
        const model = _model || MedicalItemScreeningTestResponse.getEmptyScreeningTest(undefined, contactFacetId);
        const firstProfile = cacheStore.screeningTestProfiles.firstOrDefault();

        const s = new MedicalItemsScreeningTestViewState();
        s.showFileSelection = false;
        s.currentFile = null;
        s.currentFollowUp = null;
        s.showAddCommentPopup = false;
        s.commentsPopupClientX = 0;
        s.commentsPopupClientY = 0;
        s.isTestSelected = !model.IsNewEMRTemplate;

        s.dateFormat = "YYYY-MM-DD";

        // Load ScreeningTest Fields
        s.id = model.Id;
        s.displayId = model.displayId;
        s.selectedProfileId = firstProfile?.Id ?? null;
        s.selectedTestId = model.ScreeningTestDefinitionId;
        s.testName = model.ScreeningTestDefinitionText;
        s.selectedFrequencyDropDownId = model.FrequencyTypeDropDownId;
        s.selectedReminderDropDownId = model.ReminderTypeDropDownId;
        s.selectedInterpretationLookupId = model.InterpretationLookupId;
        s.screeningDueDate = new ScreeningTestDueDate(model.DueDate, s.dateFormat);
        s.completionDate = model.CompletionDate;
        s.completionDateText = model.CompletionDate != null ? moment(model.CompletionDate).format(s.dateFormat) : "";
        s.selectedStatusLookupId = model.StatusLookupId;
        s.notes = model.Notes;
        s.attachments = model.Attachments ? model.Attachments : [];
        s.originalInterpretationLookupId = model.InterpretationLookupId;
        s.followups = model.FollowUps ? model.FollowUps : [];
        s.followupIdToRemove = null;
        s.startAge = model.StartAge;
        s.endAge = model.EndAge;
        s.requestingStaffContactFacetId = contactFacetId || model.RequestingStaffContactFacetId;

        return s;
    }
}

interface IMedicalItemsScreeningTestViewProps extends IMedicalItemsViewProps {
    PatientMedicalSummaryModel?: IPatientMedicalSummaryModel;
}

class MedicalItemsScreeningTestView extends BaseMedicalItemsView<IMedicalItemsScreeningTestViewProps, MedicalItemsScreeningTestViewState> {
    private newEntrySection: SearchableSection = SearchableSection.create("NewEntry", "NewItemLabel", 2);
    private alreadyOnFileSection: SearchableSection = SearchableSection.create("AlreadyOnFile", "AlreadyOnFileLabel", 1);
    private readonly isPreventiveActivityRequestingStaffEnabled = cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff;
    private readonly isPreventiveActivityEnhancementsEnabled = cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements;

    constructor(props: IMedicalItemsViewProps) {
        super(props);
        const contactFacetId = this.getRequestingStaffContactFacetId();

        this.state = patientStore.getMedicalItemCachedStateGeneric<MedicalItemsScreeningTestViewState>(
            props.PatientContactId,
            this.getItemCacheId(props)
        ) || MedicalItemsScreeningTestViewState.getDefault(undefined, this.isPreventiveActivityRequestingStaffEnabled ? contactFacetId : undefined);
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsScreeningTestViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = nextProps.Model;

        if (model != this.props.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

            if (isEditing && medicalItems && medicalItems.length > 0) {
                const latestNewItem = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();

                if (latestNewItem != null) {
                    if (this.isPreventiveActivityRequestingStaffEnabled) {
                        const newItemModel = latestNewItem as MedicalItemScreeningTestResponse;
                        const requestingStaffContactFacetId = newItemModel.RequestingStaffContactFacetId || this.getRequestingStaffContactFacetId();

                        newItemModel.RequestingStaffContactFacetId = requestingStaffContactFacetId;

                        this.setEditedItemState(newItemModel);
                    }
                    else {
                        this.setEditedItemState(latestNewItem);
                    }
                }
                else {
                    // modify the last item if uncommitted
                    const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                    if (latestMedicalItem != null && !latestMedicalItem.IsCommitted)
                        this.setEditedItemState(latestMedicalItem);
                }
            }
        }
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.id, this.state);
    }

    public componentDidMount(): void {
        this.adjustScrollPosition();
    }

    public componentDidUpdate(prevProps: IMedicalItemsScreeningTestViewProps): void {
        if (prevProps.Model.get("SelectedEMRTemplateId") !== this.props.Model.get("SelectedEMRTemplateId") || prevProps.Model.get("ViewMode") !== this.props.Model.get("ViewMode"))
            this.adjustScrollPosition();
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.state.showFileSelection &&
                    this.renderFileSelection(this.props.PatientModel)
                }
                {this.state.currentFile &&
                    this.renderAttachmentPreview(this.state.currentFile)
                }
                {this.state.showFollowupSelection &&
                    this.renderFollowupsSelection(this.props.PatientModel)
                }
                {this.state.currentFollowUp && !this.state.showAddCommentPopup &&
                    this.renderMedicalRequestPopup(this.state.currentFollowUp)
                }
                {this.state.currentFollowUp && this.state.showAddCommentPopup &&
                    this.renderAddCommentPopup(this.state.currentFollowUp, this.state.commentsPopupClientX, this.state.commentsPopupClientY)
                }
                <BaseMedicalItemsView
                    PatientContactId={this.props.PatientContactId}
                    Model={this.props.Model}
                    PatientModel={this.props.PatientModel}
                    GetDetailsTitleFunction={model => this.renderTitle(model as MedicalItemScreeningTestResponse)}
                    ListItemRenderFunction={(model, group) => this.renderListMode(model as MedicalItemScreeningTestResponse, group)}
                    MedicalItemRenderFunction={model => this.renderViewMode(model as MedicalItemScreeningTestResponse)}
                    MedicalItemEditRenderFunction={model => this.renderEditMode(model as MedicalItemScreeningTestResponse)}
                    MedicalItemHeaderRenderFunction={model => this.renderHeader(model as MedicalItemScreeningTestResponse)}
                    SaveFunction={async () => await this.save()}
                    SavePlusFunction={async () => await this.save(true)}
                    emrType={MedicalItemTypeEnum.ScreeningTest}
                    CheckIfSaveEnabledFunction={() => this.checkIfSaveIsEnabled()}
                    toSearchableStringFunction={items => this.searchMedicalItem(items)}
                    listItemSortingFunction={items => MedicalItemScreeningTest.sortEmrTemplates(items as MedicalItemScreeningTestResponse[])}
                    GetCPPVisibility={latestMedicalItem => this.getCPPVisibility(latestMedicalItem as MedicalItemScreeningTestResponse)}
                    showNewEntryControl={true}
                    disableSaveIfNotLastItem={true}
                    showFavorites={this.props.showFavorites}
                />
            </div>
        );
    }

    private renderListMode(model: MedicalItemScreeningTestResponse, group: MedicalItem[]): JSX.Element {
        if (!model)
            return null;

        const screening = new MedicalItemScreeningTest(model);
        const testName = screening.getScreeningTestTitle();

        const screeningStatusLookupId = screening.getStatusLookupId();
        const lastCompletion = this.renderLastCompletedScreeningText(group as MedicalItemScreeningTestResponse[]);

        return (
            <div className="med-note" key={"screening" + model.Id}>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="tbc r0">
                            <div className="pull-left screening-list-item-header">
                                <p className="ellipsis bold">
                                    {testName ?? "--"}
                                </p>
                            </div>
                        </div>
                        <div className="tbc r1">
                            <div className="screeningtest-emr-status screening-list-item-header">
                                <p
                                    className="align-right"
                                    title={cacheStore.GetLocalizedValueByLookupId(screeningStatusLookupId)}
                                >
                                    {screeningStatusLookupId &&
                                        limitTextLength(cacheStore.GetLocalizedValueByLookupId(screeningStatusLookupId), 10)
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="tbc r2">
                            <ScreeningTestWarningIcon screeningTest={screening} />
                        </div>
                        {this.renderInvalidLabel(model)}
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="tbc r0">
                            <div className="pull-left">
                                {lastCompletion}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderInvalidLabel(model: MedicalItemScreeningTestResponse): JSX.Element {
        if (!model.EMRTemplateIsInvalid)
            return null;

        return (
            <p className="red-text" style={{ marginTop: "-6px" }}>{cacheStore.GetString("InvalidMedicalItemLabel")}</p>
        );
    }

    private renderLastCompletedScreeningText(items: MedicalItemScreeningTestResponse[]): JSX.Element {
        if (!items)
            return null;

        const itemsCompleted = items.filter(x => !x.IsNewMedicalItem && x.CompletionDate != null);
        const latestCompletedItem = itemsCompleted.sortByProperties(propertyComparerDesc(x => x.CompletionDate), propertyComparerDesc(x => x.CreationDate)).firstOrDefault();
        if (!latestCompletedItem)
            return null;

        return (
            <p>
                {latestCompletedItem.InterpretationLookupId &&
                    <span>{`${cacheStore.GetLocalizedValueByLookupId(latestCompletedItem.InterpretationLookupId)} - `} </span>
                }

                <span style={{ fontStyle: "italic" }}>
                    {`${cacheStore.GetString("CompletedOnLabel")} ${formatDate(latestCompletedItem.CompletionDate, "YYYY-MM-DD")}`}
                </span>
            </p>
        );
    }

    private renderViewMode(model: MedicalItemScreeningTestResponse): JSX.Element {
        if (!model)
            return null;

        const screening = new MedicalItemScreeningTest(model);

        const alignRight: React.CSSProperties = { textAlign: "right" };
        const frequency = screening.getFrequency();
        const reminder = screening.getReminder();
        const requestingStaff = screening.getRequestingStaff();

        const medicalItems = this.props.Model.get("MedicalItemsList")?.toArray() ?? [];
        const latestItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

        return (
            <div className="medical-reaction-detail-box" id={latestItem.Id === model.Id ? "latestItem" : ""}>
                <table className="screening-problems-edit border" style={{ maxWidth: "950px" }}>
                    <tbody>
                        {cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText && (
                            <tr>
                                <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("ItemLabel")}</label></td>
                                <td className="c2"><strong data-cy="medicalItems-preventiveActivity-item">{screening.getScreeningTestTitle()}</strong></td>
                            </tr>
                        )}
                        {this.isPreventiveActivityRequestingStaffEnabled &&
                            (
                                <tr>
                                    <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("RequestingStaffLabel")}</label></td>
                                    <td className="c2"><strong data-cy="medicalItems-preventiveActivity-requestingStaff">{this.renderRequestingStaff(requestingStaff, true, true)}</strong></td>
                                </tr>
                            )
                        }
                        <tr>
                            <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("FrequencyLabel")}</label></td>
                            <td className="c2"><strong data-cy="medicalItems-preventiveActivity-frequency">{frequency ? frequency.getName() : "--"}</strong></td>
                        </tr>
                        <tr>
                            <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("ReminderLabel")}</label></td>
                            <td className="c2"><strong data-cy="medicalItems-preventiveActivity-reminder">{reminder ? reminder.getName() : "--"}</strong></td>
                        </tr>
                        <tr>
                            <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("StartAgeLabel")}</label></td>
                            <td className="c2"><strong data-cy="medicalItems-preventiveActivity-startAge">{model.StartAge ? model.StartAge.toString() : "--"}</strong></td>
                        </tr>
                        <tr>
                            <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("EndAgeLabel")}</label></td>
                            <td className="c2"><strong data-cy="medicalItems-preventiveActivity-endAge">{model.EndAge ? model.EndAge.toString() : "--"}</strong></td>
                        </tr>
                        <tr>
                            <td className={`c1 ${this.isPreventiveActivityEnhancementsEnabled ? "baseline" : ""}`} style={alignRight}><label className="form-label">{cacheStore.GetString("StatusLabel")}</label></td>
                            <td className="c2">
                                <div>
                                    <strong data-cy="medicalItems-preventiveActivity-status">
                                        {model.StatusLookupId
                                            ? cacheStore.GetLocalizedValueByLookupId(model.StatusLookupId)
                                            : "--"
                                        }
                                    </strong>
                                    <ScreeningTestWarningIcon screeningTest={screening} />
                                </div>
                                {screening.canShowStatusReminderText() && (
                                    this.isPreventiveActivityEnhancementsEnabled
                                        ? (
                                            <div className={styles.notice}>
                                                <Notice type={screening.isInToDoPeriod() ? "warning" : "info"} message={screening.getStatusReminderText()} />
                                            </div>
                                        )
                                        : (
                                            <div style={{ fontStyle: "italic" }}>
                                                {screening.getStatusReminderText()}
                                            </div>
                                        )
                                )}
                            </td>
                        </tr>
                        {screening.isCompleted() && (
                            <tr>
                                <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("CompletionDateLabel")}</label></td>
                                <td className="c2"><strong>{model.CompletionDate ? moment(model.CompletionDate).format(this.state.dateFormat) : "--"}</strong></td>
                            </tr>
                        )}
                        {screening.isCompleted() && (
                            <tr>
                                <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("InterpretationLabel")}</label></td>
                                <td className="c2"><strong>{model.InterpretationLookupId ? cacheStore.GetLocalizedValueByLookupId(model.InterpretationLookupId) : "--"}</strong></td>
                            </tr>
                        )}
                        <tr>
                            <td className="c1" style={alignRight}><label className="form-label">{cacheStore.GetString("DueDateLabel")}</label></td>
                            <td className="c2"><strong data-cy="medicalItems-preventiveActivity-due">{model.DueDate ? moment(model.DueDate).format(this.state.dateFormat) : "--"}</strong></td>
                        </tr>
                        {cacheStore.ClinicSettings.EnableFollowUps && (
                            <tr>
                                <td className="c1 baseline" style={alignRight}>
                                    <label className="form-label">{cacheStore.GetString("RequestsLabel")}</label>
                                </td>
                                <td className="c2">
                                    <strong>
                                        <table style={{ width: "100%" }}>
                                            {this.renderFollowUpList(model.FollowUps)}
                                        </table>
                                    </strong>
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="c1 baseline" style={alignRight}><label className="form-label">{cacheStore.GetString("AttachmentsLabel")}</label></td>
                            <td className="c2">
                                <strong>
                                    <table>
                                        <tbody>
                                            <AttachedDocumentList
                                                selectedAttachments={model.Attachments.map(x => ExportableFileHelper.fromAttachedObject(x).toExportableFileModel())}
                                                resolveDescription={p => p.displayDescription}
                                                showPaperClipIcon
                                            />
                                        </tbody>
                                    </table>
                                </strong>
                            </td>
                        </tr>
                        <tr>
                            <td className="c1 baseline" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                            </td>
                            <td className="c2">
                                <strong data-cy="medicalItems-preventiveActivity-notes">
                                    {JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(model.Notes))}
                                </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    private get allPersonsList(): Immutable.List<SearchableItemModelT<Person>> {
        const persons = CliniciansHelper.GetAllCliniciansSearchStrategy().cliniciansToSearchFrom;

        return persons.map(p => new SearchableItemModelT<Person>(
            `${p.NameWithPrefix}${IsNullOrWhiteSpace(p.Licence) ? "" : " (" + p.Licence + ")"}`,
            p.ContactId,
            p
        ))
            .toImmutable();
    }

    private renderRequestingStaff(requestingStaffId: string, hideClearButton?: boolean, showInViewMode?: boolean): JSX.Element {
        const staff = cacheStore.GetCliniciansPersonsByContactFacetId(requestingStaffId);

        if (showInViewMode) {
            if (!requestingStaffId) {
                return <span>--</span>;
            }

            return <span>{staff.NameWithPrefixFirst()}</span>;
        }

        if (!requestingStaffId) {
            return (
                <div className="fullWidth">
                    <TextboxControl
                        value=""
                        valid={false}
                        autoCompleteItems={this.allPersonsList}
                        onAutoCompleteSelectCallBack={(_code: string, model: Person) =>
                            this.mutateState(s => s.requestingStaffContactFacetId = model.ContactFacetId)
                        }
                        onAutoCompleteSelectClearTextBox={true}
                        placeholder=""
                        limit={null}
                    />
                </div>
            );
        }

        return (
            <div className="fullWidth flex-container flex-align-items-center flex-start">
                <span className="push-right-5">{staff?.NameWithPrefix ?? "--"}</span>
                {!hideClearButton && (
                    <IconButton
                        category="status-icon"
                        onClick={() => this.onClearButtonClick()}
                        size="sm"
                        variant="error"
                    >
                        <XIcon />
                    </IconButton>
                )}
            </div>
        );
    }

    private renderEditMode(model: MedicalItemScreeningTestResponse): JSX.Element {
        if (!model)
            return null;

        const state = this.state;
        const alignRight: React.CSSProperties = { textAlign: "right" };

        const profiles = cacheStore.screeningTestProfiles;

        const screeningModel = new MedicalItemScreeningTest(new MedicalItemScreeningTestResponse());
        this.updateMedicalItemFromState(screeningModel);
        const selectedTestName = screeningModel.getScreeningTestTitle();

        const patientTestsSearchModel = this.toSearchableWithLatestScreenings(cacheStore.getTestDefinitionsByProfileId(state.selectedProfileId));

        const thereAreMultipleProfiles = profiles && profiles.length > 1;
        const hasSelectedTestName = selectedTestName?.length > 0;

        const medicalItems = this.props.Model.get("MedicalItemsList")?.toArray() ?? [];
        const latestItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

        const patientDoB = this.props.PatientModel.IndividualResponse.DateOfBirth;

        return (
            <div className="medical-reaction-detail-box" id={latestItem.Id === model.Id ? "latestItem" : ""}>
                {this.state.followupIdToRemove &&
                    this.renderFollowupRemovalPopup(this.state.followupIdToRemove, selectedTestName)
                }
                <table className="screening-problems-edit border" style={{ maxWidth: "950px" }}>
                    <tbody>
                        {model.IsNewEMRTemplate && thereAreMultipleProfiles && (
                            <tr>
                                <td className="c1" style={alignRight}>
                                    <label className="form-label">{cacheStore.GetString("ProfileInformationLabel")}</label>
                                </td>
                                <td className="c2">
                                    <select className="form-control myle-control" onChange={(e: any) => this.handleSelectedProfileChanged(e.target.value)} value={state.selectedProfileId.toString()}>
                                        {profiles.map(
                                            (profile: ScreeningTestProfile) => (
                                                <option
                                                    key={profile.Id}
                                                    value={profile.Id.toString()}
                                                >
                                                    {cacheStore.GetString(profile.VocabularyResourceKey)}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </td>
                            </tr>
                        )}
                        {(cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText || model.IsNewEMRTemplate) && (
                            <tr>
                                <td className="c1" style={alignRight}>
                                    <label className="form-label">{cacheStore.GetString("ItemLabel")}</label>
                                </td>
                                <td className={`c2 ${cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText && this.state.isTestSelected ? "inline-flex-center" : ""}`}>
                                    <SingleSelectTextBoxControl
                                        isSelected={hasSelectedTestName && (!cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText || this.state.isTestSelected)}
                                        onRemove={() => this.handleTestRemoved(model)}
                                        displayText={selectedTestName ?? ""}
                                        alternateEditComponent={(
                                            <TextboxControlWithSections
                                                id="PatientProfileTests"
                                                dataCy="medicalItems-preventiveActivity-item"
                                                value={selectedTestName ?? ""}
                                                valid={!!selectedTestName}
                                                maxLength={120}
                                                maxResults={1000}
                                                autoCompleteItems={patientTestsSearchModel.toImmutable()}
                                                onAutoCompleteSelectCallBack={(model, section) => this.handleTestSelected(model, section)}
                                                onMountFocus={true}
                                                footer={(cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText
                                                    ? (<a onClick={() => this.handleFreeTextTestSelected()}>[+] {cacheStore.GetString("PreventiveActivity_AddAnotherItemLabel")}</a>)
                                                    : null)
                                                }
                                                onChange={value => cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText && this.mutateState(s => { s.testName = value; s.selectedTestId = null; })}
                                            />
                                        )}
                                    />
                                    {cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText && this.state.isTestSelected && (
                                        <span style={{ marginLeft: "-8px" }}>
                                            <IconButton
                                                category="tertiary"
                                                size="sm"
                                                onClick={() => this.mutateState(s => {
                                                    s.isTestSelected = false;
                                                })}
                                            >
                                                <PencilSimpleLightIcon />
                                            </IconButton>
                                        </span>
                                    )}
                                </td>
                            </tr>
                        )}

                        {this.isPreventiveActivityRequestingStaffEnabled &&
                            (
                                <tr>
                                    <td className="c1" style={alignRight}>
                                        <label className="form-label">{cacheStore.GetString("RequestingStaffLabel")}</label>
                                    </td>
                                    <div className={styles.requestingStaffWrapper}>
                                        {this.renderRequestingStaff(this.state.requestingStaffContactFacetId)}

                                        <LockControl
                                            isLocked={sessionStore.isLockedData("ScreeningTestModel")}
                                            onClick={() => this.onLockIconClick()}
                                        />
                                    </div>
                                </tr>
                            )
                        }
                        <tr>
                            <td className="c1" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("FrequencyLabel")}</label>
                            </td>
                            <td className="c2">
                                <SelectLookupControl
                                    dataCy="medicalItems-preventiveActivity-frequency"
                                    className={this.isPreventiveActivityEnhancementsEnabled ? styles.medicalItemsScreeningTestItem : ""}
                                    IncludeEmptyChoice={true}
                                    invalid={this.isInvalid(state.selectedFrequencyDropDownId)}
                                    Value={state.selectedFrequencyDropDownId}
                                    AvailableValues={ScreeningTestFrequencyOption.getAllAsCombobox()}
                                    disabled={!hasSelectedTestName}
                                    Callback={(value: string) => this.mutateState(s => s.selectedFrequencyDropDownId = value, () => this.changeDueDate(state.selectedStatusLookupId, state.originalInterpretationLookupId, value, state.completionDate))}
                                />
                            </td>
                        </tr>

                        <tr>
                            <td className="c1" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("ReminderLabel")}</label>
                            </td>
                            <td className="c2">
                                <SelectLookupControl
                                    dataCy="medicalItems-preventiveActivity-reminder"
                                    className={this.isPreventiveActivityEnhancementsEnabled ? styles.medicalItemsScreeningTestItem : ""}
                                    IncludeEmptyChoice={true}
                                    Value={state.selectedReminderDropDownId}
                                    AvailableValues={ScreeningTestReminderOption.getAllAsCombobox()}
                                    Callback={(value: string) => this.mutateState(s => s.selectedReminderDropDownId = value)}
                                    disabled={!hasSelectedTestName}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="c1" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("StartAgeLabel")}</label>
                            </td>
                            <td className="c2">
                                <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                    <TextboxControl
                                        dataCy="medicalItems-preventiveActivity-startAge"
                                        className={`form-control input ${this.isPreventiveActivityEnhancementsEnabled ? styles.medicalItemsScreeningTestItem : ""}`}
                                        value={this.state.startAge ? this.state.startAge.toString() : ""}
                                        disabled={!hasSelectedTestName}
                                        IsNumeric={true}
                                        maxLength={3}
                                        onChange={value => this.onStartAgeChanged(value, patientDoB)}
                                    />
                                    <p style={{ padding: "7.5px" }}>{cacheStore.GetString("YearOptionalSLabel")}</p>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td className="c1" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("EndAgeLabel")}</label>
                            </td>
                            <td className="c2">
                                <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                    <TextboxControl
                                        dataCy="medicalItems-preventiveActivity-endAge"
                                        className={`form-control input ${this.isPreventiveActivityEnhancementsEnabled ? styles.medicalItemsScreeningTestItem : ""}`}
                                        valid={!this.isInvalid(MedicalItemScreeningTest.isEndAgeValid(this.state.startAge, this.state.endAge))}
                                        value={this.state.endAge ? this.state.endAge.toString() : ""}
                                        disabled={!hasSelectedTestName}
                                        IsNumeric={true}
                                        maxLength={3}
                                        onChange={value => this.onEndAgeChanged(value)}
                                    />
                                    <p style={{ padding: "7.5px" }}>{cacheStore.GetString("YearOptionalSLabel")}</p>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td className="c1" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("StatusLabel")}</label>
                            </td>
                            <td className="c2">
                                <div className="flex-row flex-col-gap-sm">
                                    <SelectLookupControl
                                        dataCy="medicalItems-preventiveActivity-status"
                                        className={this.isPreventiveActivityEnhancementsEnabled ? styles.medicalItemsScreeningTestItem : ""}
                                        IncludeEmptyChoice={true}
                                        invalid={this.isInvalid(state.selectedStatusLookupId)}
                                        Value={state.selectedStatusLookupId}
                                        AvailableValues={ScreeningTestStatus.GetAll().map(x => x.ToComboboxItem())}
                                        disabled={!hasSelectedTestName}
                                        Callback={(value: string) => this.onStatusChange(value)}
                                    />
                                    {this.isPreventiveActivityEnhancementsEnabled && (
                                        <LinkButton
                                            label={cacheStore.GetString("CompletedAsOfTodayLabel")}
                                            onClick={() => {
                                                const newCompletionDate = moment().toDate();
                                                this.mutateState(s => {
                                                    s.selectedStatusLookupId = ScreeningTestStatus.Completed.LookupID;
                                                    s.completionDate = newCompletionDate;
                                                    s.completionDateText = moment(newCompletionDate).format("YYYY-MM-DD");
                                                }, () => this.changeDueDate(ScreeningTestStatus.Completed.LookupID, state.selectedInterpretationLookupId, state.selectedFrequencyDropDownId, newCompletionDate));
                                            }}
                                            size="sm"
                                            underline
                                            variant="brand"
                                            disabled={!this.state.selectedFrequencyDropDownId}
                                        />
                                    )}
                                </div>
                                {screeningModel.canShowStatusReminderText() && (
                                    this.isPreventiveActivityEnhancementsEnabled
                                        ? (
                                            <div className={styles.notice}>
                                                <Notice type={screeningModel.isInToDoPeriod() ? "warning" : "info"} message={screeningModel.getStatusReminderText()} />
                                            </div>
                                        )
                                        : (
                                            <p style={{ fontStyle: "italic" }}>
                                                {screeningModel.getStatusReminderText()}
                                            </p>
                                        )
                                )}
                            </td>
                        </tr>
                        {screeningModel.isStatusCompleted() && (
                            <tr>
                                <td className="c1" style={alignRight}>
                                    <label className="form-label">{cacheStore.GetString("CompletionDateLabel")}</label>
                                </td>
                                <td className="c2">
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControlWithDatePicker
                                            id="CompletionDate"
                                            isInvalid={!state.completionDate || moment(state.completionDate).isAfter()}
                                            dateValue={state.completionDate}
                                            textValue={state.completionDateText}
                                            width={this.isPreventiveActivityEnhancementsEnabled ? 300 : undefined}
                                            onChange={(date: DateTimeWithPrecision) => {
                                                this.onCompletionDateChange(date, newCompletionDate => this.changeDueDate(state.selectedStatusLookupId, state.selectedInterpretationLookupId, state.selectedFrequencyDropDownId, newCompletionDate));
                                            }}
                                        />
                                        <div style={{ padding: "7.5px" }}>
                                            <a
                                                onClick={() => {
                                                    const completionDate = moment().toDate();

                                                    this.mutateState(s => {
                                                        s.completionDate = completionDate;
                                                        s.completionDateText = moment(completionDate).format("YYYY-MM-DD");
                                                    }, () => this.changeDueDate(this.state.selectedStatusLookupId, this.state.selectedInterpretationLookupId, this.state.selectedFrequencyDropDownId, completionDate));
                                                }}
                                            >
                                                {cacheStore.GetString("TodayLabel")}
                                            </a>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {screeningModel.isStatusCompleted() && (
                            <tr>
                                <td className="c1" style={alignRight}>
                                    <label className="form-label">{cacheStore.GetString("InterpretationLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        className={this.isPreventiveActivityEnhancementsEnabled ? styles.medicalItemsScreeningTestItem : ""}
                                        IncludeEmptyChoice={true}
                                        Value={state.selectedInterpretationLookupId}
                                        AvailableValues={ScreeningTestInterpretation.getAll().map(x => x.ToComboboxItem())}
                                        disabled={false}
                                        Callback={(value: string) => this.mutateState(
                                            s => s.selectedInterpretationLookupId = value,
                                            this.isPreventiveActivityEnhancementsEnabled
                                                ? undefined
                                                : () => this.changeDueDate(state.selectedStatusLookupId, value, state.selectedFrequencyDropDownId, state.completionDate)
                                        )}
                                    />
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td className="c1" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("DueDateLabel")}</label>
                            </td>
                            <td className="c2" style={{ display: "flex" }}>
                                <TextboxControlWithDatePicker
                                    id="DueDate"
                                    width={this.isPreventiveActivityEnhancementsEnabled ? 300 : undefined}
                                    dateValue={this.state.screeningDueDate.dueDate}
                                    textValue={this.state.screeningDueDate.dueDateText}
                                    onChange={(date: DateTimeWithPrecision) => this.onDueDateChange(date)}
                                    disabled={!hasSelectedTestName}
                                    keepWhiteOnDisabled={true}
                                />
                                {hasSelectedTestName && (
                                    <ShorthandDateSetter
                                        dataCy="medicalItems-preventiveActivity-due"
                                        initialDate={this.state.screeningDueDate.initialDueDate}
                                        onClick={(newDate: Date) => this.handleOnAddTimeToDueDate(newDate)}
                                        visibleTimeIntervals={ScreeningTestFrequencyOption.getAllIntervals()}
                                    />
                                )}
                            </td>
                        </tr>
                        {cacheStore.ClinicSettings.EnableFollowUps && (
                            <tr>
                                <td className="c1 baseline" style={alignRight}>
                                    <label className="form-label">{cacheStore.GetString("RequestsLabel")}</label>
                                </td>
                                {hasSelectedTestName && (
                                    <td className="c2" style={{ marginTop: "3px" }}>
                                        <table style={{ width: "100%" }}>
                                            {this.renderFollowUpList(this.state.followups, true)}
                                        </table>
                                        <a className="pointer" style={{ float: `${this.state.followups.length > 0 ? "right" : "none"}` }} onClick={() => this.mutateState(s => s.showFollowupSelection = true)}>
                                            <span style={{ marginLeft: "5px" }}>{cacheStore.GetString("LinkARequestLabel")}</span>
                                        </a>
                                    </td>
                                )}
                            </tr>
                        )}
                        <tr>
                            <td className="c1 baseline" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("AttachmentsLabel")}</label>
                            </td>
                            {hasSelectedTestName && (
                                <td className="c2" style={{ marginTop: "3px" }}>
                                    <AttachDocumentLink onClick={() => this.mutateState(s => s.showFileSelection = true)} />
                                    <table>
                                        <AttachedDocumentList
                                            selectedAttachments={this.state.attachments.map(x => ExportableFileHelper.fromAttachedObject(x).toExportableFileModel())}
                                            removeAttachmentFromList={(p, file) => this.mutateState(s => s.attachments = s.attachments.filter(x => !(x.ObjectId === file.ToAttachedObjectResponse().ObjectId && x.Type === file.ToAttachedObjectResponse().Type)))}
                                            resolveDescription={p => p.displayDescription}
                                            showPaperClipIcon
                                        />
                                    </table>
                                </td>
                            )}
                        </tr>
                        <tr>
                            <td className="c1 baseline" style={alignRight}>
                                <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                            </td>
                            <td className="c2">
                                <TextAreaResizable
                                    minHeightPx={100}
                                    id={"ScreeningNote_" + (state.selectedTestId ? state.selectedTestId : model.Id)}
                                    dataCy="medicalItems-preventiveActivity-notes"
                                    text={state.notes}
                                    isDisabled={!hasSelectedTestName}
                                    setCursorStartOnFocus={false}
                                    resetTextOnIdChange={true}
                                    onChange={value => this.handleNotesChanged(value)}
                                    keepWhiteOnDisabled={true}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    private renderTitle(model: MedicalItemScreeningTestResponse): string {
        if (!model)
            return null;

        const medicalItems = this.props.Model.get("MedicalItemsList")?.toArray() ?? [];
        const latestItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault() as MedicalItemScreeningTestResponse;

        const screening = new MedicalItemScreeningTest(latestItem);
        const testName = screening.getScreeningTestTitle();

        const screeningTestDefinitionLabel = model.IsNewMedicalItem
            ? cacheStore.GetString("NewItemLabel")
            : (testName ?? "--");

        return `${cacheStore.GetString("PreventiveActivityLabel")} : ${screeningTestDefinitionLabel}`;
    }

    private renderHeader(model: MedicalItemScreeningTestResponse): JSX.Element {
        if (!model)
            return null;

        const screening = new MedicalItemScreeningTest(model);

        let headerString: string = "";
        const updatedByEmployee = cacheStore.GetCliniciansPersonsByContactFacetId(model.EmployeeId);

        if (updatedByEmployee) {
            if (this.isPreventiveActivityEnhancementsEnabled) {
                const nameWithPrefix = updatedByEmployee.NameWithPrefix;
                const onLabel = cacheStore.GetString("OnLabel").toLowerCase();
                const atLabel = cacheStore.GetString("AtLabel");
                const formattedDate = moment(model.LastUpdateDate).format("YYYY-MM-DD");
                const formattedTime = moment(model.LastUpdateDate).format("HH:mm:ss");
                headerString = `${nameWithPrefix} ${onLabel} ${formattedDate} ${atLabel} ${formattedTime}`;
            }
            else {
                headerString = `${updatedByEmployee.NameWithPrefix} ${cacheStore.GetString("OnLabel").toLowerCase()} ${formatDate(model.LastUpdateDate)}`;
            }
        }

        let headerStringMarginRight = "10px";
        if (model.IsCommitted || (model.EmployeeId != sessionStore.getCurrentEmployee().ContactFacetId))
            headerStringMarginRight = "20px";

        const headerContent = (
            <div className="row" style={{ width: "100%", marginLeft: "0", marginRight: "0" }}>
                <div className="col-xs-5">
                    <span style={{ width: "183px", display: "inline-block" }}>{formatDate(model.DueDate, "YYYY-MM-DD")}</span>
                    {!model.IsNewMedicalItem && (
                        <span>
                            {model.StatusLookupId
                                ? cacheStore.GetLocalizedValueByLookupId(model.StatusLookupId)
                                : "--"
                            }
                            {model.InterpretationLookupId
                                ? ` (${cacheStore.GetLocalizedValueByLookupId(model.InterpretationLookupId)})`
                                : ""
                            }
                        </span>
                    )}
                </div>
                {!model.IsNewMedicalItem && (
                    <div className="col-xs-6 pull-right text-right" style={{ marginRight: headerStringMarginRight }}>
                        {screening.hasAttachments() &&
                            <span><img style={{ marginRight: "30px" }} className="paperClip-rotation" src={img_icon_attachment_icon} alt={cacheStore.GetString("AttachmentLabel")} /></span>
                        }
                        <span title={headerString}>{limitTextLength(headerString, 70)}</span>
                    </div>
                )}
            </div>
        );

        return headerContent;
    }

    private renderFileSelection(patient: Patient): JSX.Element {
        if (!patient)
            return null;

        return (
            <FileSelectionContainer
                title={cacheStore.GetString("AttachAFileLabel")}
                patient={Person.FromPatient(patient)}
                type={FileSelectionContainerType.Attachment}
                onClose={() => this.mutateState(s => s.showFileSelection = false)}
                onPrintOrAttachClicked={files => this.handleOnFileAttached(files)}
                autoManageOverlay={true}
            />
        );
    }

    private renderAttachmentPreview(attachedObj: AttachedObjectResponse): JSX.Element {
        if (!attachedObj)
            return null;

        this.mutateState(
            s => s.currentFile = null,
            () => ExportableFileHelper.fromAttachedObject(attachedObj).openInNewWindow()
        );
    }

    private renderFollowUpList(followups: FollowUpResponse[], showDeleteOption?: boolean): JSX.Element[] {
        if (!followups || followups.length === 0)
            return [];

        let sortedFollowups = followups.sortByProperties(propertyComparer(x => DateTime.getDateOrMax(x.DueDate)), propertyComparer(x => DateTime.getDateOrMax(x.CreationDate)));

        return sortedFollowups.map(followup => {
            const itemName = followup.MedicalRequestType == MedicalRequestType.ConsultationRequest
                ? `${cacheStore.GetString("ConsultationRequestLabel")}: ${followup.Speciality}`
                : `${cacheStore.GetString("ExamRequestLabel")}: ${followup.Reason || limitTextLength(followup.Description, 50)}`;

            return (
                <>
                    <tbody key={followup.Id} style={{ fontWeight: "normal" }}>
                        <tr>
                            <td>
                                <div className="flex-container">
                                    <a onClick={() => this.mutateState(s => s.currentFollowUp = followup)}>
                                        {itemName}
                                    </a>
                                    <div style={{ alignContent: "flex-end" }}>
                                        {(followup.IsPrinted || followup.RelatedEncounterIsSignedOff) && (
                                            <a style={{ cursor: "pointer" }} onClick={e => this.setUpAddCommentPopup(e, followup)}>
                                                [<span className={followup.CommentsCount > 0 ? "red-text" : ""}>{followup.CommentsCount > 0 ? followup.CommentsCount : "+"}</span>]
                                            </a>
                                        )}
                                        {showDeleteOption &&
                                            <strong style={{ color: "red", cursor: "pointer", paddingLeft: "5px" }} onClick={() => this.handleRemoveFollowup(followup.Id)}>X</strong>
                                        }
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ paddingBottom: "10px" }}>
                                <div style={{ marginTop: "5px" }}>
                                    <SelectLookupControl
                                        Value={followup.StatusLookupId}
                                        AvailableValues={MedicalRequestStatus.getComboboxItems()}
                                        Callback={(value: string) => this.handleFollowupStatusChange(followup.Id, followup.MedicalRequestType, value)}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </>
            );
        });
    }

    private renderFollowupsSelection(patient: Patient): JSX.Element {
        if (!patient)
            return null;

        return (
            <FollowupSelectionComponent
                patientContactFacetId={patient.InactivableResponse.Id}
                followupsToHideList={this.state.followups}
                encounterId={this.props.Model.get("Encounter")?.Id}

                onClose={() => this.mutateState(s => s.showFollowupSelection = false)}
                onAttached={selected => this.handleOnFollowupsAttached(selected)}
            />
        );
    }

    private renderMedicalRequestPopup(followUp: FollowUpResponse): JSX.Element {
        if (!followUp)
            return null;

        return (
            <MedicalRequestPopupController
                type={followUp.MedicalRequestType}
                id={followUp.Id}
                isDisplayAsReadonly={true}
                closeAfterButtonClick={true}

                onClose={() => this.mutateState(s => s.currentFollowUp = null)}
                onPrintSuccess={fileName => this.onMedicalRequestPrintSuccess(fileName, followUp.Id, followUp.MedicalRequestType)}
                patientContactIdForNewRequests={this.props.PatientModel.IndividualResponse.Contact_id}
            />
        );
    }

    private renderFollowupRemovalPopup(followupId: string, screeningTestName: string): JSX.Element {
        return (
            <Modal
                id="confirm-save-popup"
                title={cacheStore.GetString("WarningLabel")}
                showOverlay={true}
                footer={this.renderFollowupRemovalPopupFooter(followupId)}
                onClose={() => this.mutateState(s => s.followupIdToRemove = null)}
            >
                <div style={{ paddingLeft: "20px" }}>
                    <div>
                        <span>
                            {cacheStore.GetString("RemovingRequestsWarningLabel", { ScreeningTestName: screeningTestName })}
                        </span>
                    </div>
                </div>
            </Modal>
        );
    }

    private renderFollowupRemovalPopupFooter(followupId: string): JSX.Element {
        return (
            <div className="flex-container" style={{ "justifyContent": "flex-end" }}>
                <div style={{ "marginLeft": "5px" }}>
                    <button className="btn btn-default" onClick={() => this.mutateState(s => s.followupIdToRemove = null)}>
                        {cacheStore.GetString("CancelLabel")}
                    </button>
                </div>
                <div style={{ "marginLeft": "5px" }}>
                    <button
                        className="btn btn-primary"
                        onClick={() => this.mutateState(s => {
                            s.followupIdToRemove = null;
                            s.followups = this.state.followups.filter(x => x.Id !== followupId);
                        })}
                    >
                        {cacheStore.GetString("OKLabel")}
                    </button>
                </div>
            </div>
        );
    }

    private renderAddCommentPopup(followup: FollowUpResponse, xPos: number, yPos: number): JSX.Element {
        if (!followup)
            return;

        return (
            <AddRequestCommentPopupComponent
                requestId={followup.Id}
                leftPosition={xPos}
                topPosition={yPos}
                onClose={() => this.mutateState(s => {
                    s.currentFollowUp = null;
                    s.showAddCommentPopup = false;
                })}
                onSaveSucess={comment => this.handleCommentAdded(followup.Id, followup.MedicalRequestType, comment && comment.Value)}
            />
        );
    }

    private handleSelectedProfileChanged(id: string): void {
        this.mutateState(s => s.selectedProfileId = Number(id));
    }

    private handleNotesChanged(notes: string): void {
        this.mutateState(s => s.notes = notes);
    }

    private setUpAddCommentPopup(e: React.MouseEvent, followup: FollowUpResponse): void {
        const leftPosition = -620;
        const topPosition = -140;
        const xPos = e.clientX + leftPosition;
        const yPos = e.clientY + topPosition;

        this.mutateState(s => {
            s.currentFollowUp = followup;
            s.showAddCommentPopup = true;
            s.commentsPopupClientX = xPos;
            s.commentsPopupClientY = yPos;
        });
    }

    private setEditedItemState(medItem: MedicalItem): void {
        const model = medItem as MedicalItemScreeningTestResponse;

        if (!model || model.IsCollapsed || model.IsCommitted)
            return;

        const isSameItem = model.displayId == this.state.displayId && model.Id == this.state.id;
        if (isSameItem)
            return;

        this.setState(MedicalItemsScreeningTestViewState.getDefault(model));
    }

    private getCPPVisibility(latestScreeningTest: MedicalItemScreeningTestResponse): boolean {
        return new MedicalItemScreeningTest(latestScreeningTest).getCPPVisibility();
    }

    private async save(isSavePlus?: boolean): Promise<void> {
        if (!this.checkIfSaveIsEnabled())
            return;

        const tmpEditedItem = this.props.Model.get("MedicalItemsList")
            .toArray()
            .filter(x => x.IsCommitted == false)
            .firstOrDefault() as MedicalItemScreeningTestResponse;
        if (tmpEditedItem == null)
            return;

        const editedItem = new MedicalItemScreeningTest(tmpEditedItem).Clone();
        const editedItemsToSend = [this.updateMedicalItemFromState(editedItem).model];
        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(editedItemsToSend);
        }

        const followupsToLink = this.state.followups.filter(x => !editedItem.model.FollowUps.some(y => y.Id === x.Id));
        const followupsToUnlink = editedItem.model.FollowUps.filter(x => !this.state.followups.some(y => y.Id === x.Id));
        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.Save,
            {
                view: MedicalItemsTab.ScreeningTest,
                editedItems: editedItemsToSend,
                isSavePlus: isSavePlus,
                followupsToLink,
                followupsToUnlink
            }
        );
    }

    private updateMedicalItemFromState(screening: MedicalItemScreeningTest): MedicalItemScreeningTest {
        screening.model.ScreeningTestDefinitionId = this.state.selectedTestId;
        screening.model.ScreeningTestDefinitionText = this.state.testName;
        screening.model.FrequencyTypeDropDownId = this.state.selectedFrequencyDropDownId;
        screening.model.ReminderTypeDropDownId = this.state.selectedReminderDropDownId;
        screening.model.InterpretationLookupId = this.state.selectedInterpretationLookupId;
        screening.model.InterpretationDate = this.state.selectedInterpretationLookupId ? DateTime.nowMoment().toDate() : null;
        screening.model.InterpretationDate = this.state.selectedInterpretationLookupId !== this.state.originalInterpretationLookupId
            ? DateTime.nowMoment().toDate()
            : screening.model.InterpretationDate;
        screening.model.CompletionDate = this.state.completionDate;
        screening.model.DueDate = this.state.screeningDueDate.dueDate;
        screening.model.StatusLookupId = this.state.selectedStatusLookupId;
        screening.model.Attachments = this.state.attachments;
        screening.model.Notes = this.state.notes;
        screening.model.StartAge = this.state.startAge;
        screening.model.EndAge = this.state.endAge;
        screening.model.RequestingStaffContactFacetId = this.state.requestingStaffContactFacetId;

        return screening;
    }

    private checkIfSaveIsEnabled(): boolean {
        if (cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText
            ? !this.state.isTestSelected
            : (!this.state.selectedTestId && !this.state.testName))
            return false;

        if (!this.state.selectedFrequencyDropDownId)
            return false;

        if (!this.state.selectedStatusLookupId)
            return false;

        if (MedicalItemScreeningTest.isCompleted(this.state.selectedStatusLookupId)
            && (!this.state.completionDate || moment(this.state.completionDate).isAfter() || !moment(this.state.completionDateText, this.state.dateFormat, true).isValid())) {
            return false;
        }

        if (!this.state.screeningDueDate.isValid())
            return false;

        if (!MedicalItemScreeningTest.isEndAgeValid(this.state.startAge, this.state.endAge))
            return false;

        if (cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff && !this.state.requestingStaffContactFacetId)
            return false;

        return true;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemScreeningTestResponse[] {
        const medicalItems = items as MedicalItemScreeningTestResponse[];

        return medicalItems.filterSearch(this.props.Model.get("SearchTerm"), item => {
            const screening = new MedicalItemScreeningTest(item);
            const textName = screening.getScreeningTestTitle();

            const name = MyleHelper.replaceNullWithEmptyString(textName);
            const status = MyleHelper.replaceNullWithEmptyString(cacheStore.GetLocalizedValueByLookupId(item.StatusLookupId));
            const interpretation = MyleHelper.replaceNullWithEmptyString(cacheStore.GetLocalizedValueByLookupId(item.InterpretationLookupId));

            return name + " " + status + " " + interpretation;
        });
    }

    private handleOnFileAttached(files: ExportableFile[]): void {
        this.mutateState(s => {
            const newFiles = files
                .filter(x => !s.attachments.some(a => x.isEquivalentAttachedObject(a)))
                .map(x => x.toAttachedObject());

            s.attachments = s.attachments.concat(newFiles);
            s.showFileSelection = false;
        });
    }

    private handleOnFollowupsAttached(followups: FollowUpItem[]): void {
        this.mutateState(s => {
            const newFollowups = followups
                .filter(x => !s.followups.some(a => a.Id === x.model.Id))
                .map(x => x.model);

            s.followups = s.followups.concat(newFollowups);
            s.showFollowupSelection = false;

            if (followups.some(x => MedicalRequestStatus.isInPendingStatus(x.model.StatusLookupId)))
                this.changeStatus(s, ScreeningTestStatus.Pending.LookupID);
        });
    }

    private handleRemoveFollowup(followupId: string): void {
        let tmpEditedItem = this.props.Model.get("MedicalItemsList").toArray()
            .filter(x => x.IsCommitted == false)
            .firstOrDefault() as MedicalItemScreeningTestResponse;

        if (tmpEditedItem && tmpEditedItem.FollowUps.some(x => x.Id === followupId)) {
            this.mutateState(s => s.followupIdToRemove = followupId);
        }
        else
            this.mutateState(s => s.followups = s.followups.filter(x => x.Id !== followupId));
    }

    private changeDueDate(statusLookupId: string, interpretationLookupId: string, selectedFrequencyDropDownId: string, completionDate: Date): void {
        const frequency = ScreeningTestFrequencyOption.fromDropDownId(selectedFrequencyDropDownId);
        let nextDueDate = this.state.screeningDueDate
            .calculateNextDueDate(statusLookupId, interpretationLookupId, frequency, completionDate, true);

        this.mutateState(s => {
            const patientDob = this.props.PatientModel.IndividualResponse.DateOfBirth;
            let endAgeDate = MedicalItemScreeningTest.calculateEndAgeDate(this.state.endAge, patientDob);

            if (MedicalItemScreeningTest.isCompleted(statusLookupId) &&
                MedicalItemScreeningTest.isNormal(interpretationLookupId) &&
                MedicalItemScreeningTest.isDueDateAfterEndAgeDate(endAgeDate, nextDueDate)) {
                s.screeningDueDate.clearDueDate();
                s.selectedReminderDropDownId = "";
            }
            else {
                s.screeningDueDate.updateBasedOnStatus(statusLookupId, interpretationLookupId, frequency, completionDate, true);
            }
        });
    }

    private onStartAgeChanged(value: any, patientDoB: Date): void {
        this.mutateState(s => {
            if (!value || isNaN(value)) {
                s.startAge = null;
            }
            else {
                s.startAge = Number(value);

                if (MedicalItemScreeningTest.isPatientAgeLessThanStartAge(s.startAge, patientDoB)) {
                    s.screeningDueDate = s.screeningDueDate.updateBaseOnStartAge(s.startAge, patientDoB);
                    this.changeStatus(s, ScreeningTestStatus.Ineligible.LookupID);
                }
            }
        });
    }

    private onEndAgeChanged(value: any): void {
        this.mutateState(
            s => s.endAge = (!value || isNaN(value)) ? null : Number(value),
            () => {
                if (MedicalItemScreeningTest.isEndAgeValid(this.state.startAge, this.state.endAge))
                    this.changeDueDate(this.state.selectedStatusLookupId, this.state.selectedInterpretationLookupId, this.state.selectedFrequencyDropDownId, this.state.completionDate);
            }
        );
    }

    private onStatusChange(statusId: string): void {
        this.mutateState(
            s => this.changeStatus(s, statusId),
            () => this.changeDueDate(this.state.selectedStatusLookupId, this.state.selectedInterpretationLookupId, this.state.selectedFrequencyDropDownId, this.state.completionDate)
        );
    }

    private onDueDateChange(date: DateTimeWithPrecision): void {
        this.mutateState(s =>
            s.screeningDueDate = s.screeningDueDate.change(date)
        );
    }

    private onCompletionDateChange(date: DateTimeWithPrecision, callback?: (newCompletionDate: Date | null) => void): void {
        let newCompletionDate: Date | null = null;

        this.mutateState(s => {
            s.completionDateText = date.text;
            if (date.value) {
                s.completionDate = moment(date.value).toDate();
            }
            else if (IsNullOrWhiteSpace(date.text)) {
                s.completionDate = null;
                s.completionDateText = "";
            }
            newCompletionDate = s.completionDate;
        }, () => {
            if (newCompletionDate)
                callback?.(newCompletionDate);
        });
    }

    private handleTestSelected(selectedTest: ScreeningTestDefinition, section: SearchableSection): void {
        if (section.code == this.alreadyOnFileSection.code) {
            const allMedicalItems = this.props.Model.get("AllMedicalItemsList").toArray() as MedicalItemScreeningTestResponse[];
            const selectedScreeningItem = allMedicalItems.firstOrDefault(p => (new MedicalItemScreeningTest(p)).getScreeningTestTitle() === selectedTest.Name);

            if (selectedScreeningItem)
                sessionStore.dispatch(
                    LocalPatientOperations.MedicalItems.SetExistingScreeningItemForNewEntry,
                    {
                        selectedScreeningItem: selectedScreeningItem
                    }
                );
        }
        else {
            const shouldSetDefaults = !this.state.selectedStatusLookupId && this.isPreventiveActivityEnhancementsEnabled;

            let reminderDefaultDropdownOption = null;
            let statusDefaultDropdownOption = null;

            if (shouldSetDefaults) {
                reminderDefaultDropdownOption = cacheStore.getDropDownOptionByType("ScreeningTestReminderType").firstOrDefault(reminder => reminder.model.Code === "3Months");
                statusDefaultDropdownOption = ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_TODO");
            }
            this.mutateState(s => {
                s.selectedStatusLookupId = shouldSetDefaults ? statusDefaultDropdownOption.LookupID : s.selectedStatusLookupId;
                s.selectedReminderDropDownId = shouldSetDefaults ? reminderDefaultDropdownOption.model.Id : s.selectedReminderDropDownId;
                s.selectedTestId = selectedTest.Id;
                s.testName = null;
                s.isTestSelected = true;
            });
        }
    }

    private handleFreeTextTestSelected(): void {
        this.mutateState(s => {
            s.isTestSelected = true;

            if (!this.state.selectedStatusLookupId && this.isPreventiveActivityEnhancementsEnabled) {
                s.selectedReminderDropDownId = cacheStore.getDropDownOptionByType("ScreeningTestReminderType").firstOrDefault(reminder => reminder.model.Code === "3Months").model.Id;
                s.selectedStatusLookupId = ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_TODO").LookupID;
            }
        });
    }

    private handleTestRemoved(model: MedicalItemScreeningTestResponse): void {
        const screeningTestModel = MedicalItemsScreeningTestViewState.getDefault(model, this.state.requestingStaffContactFacetId);
        screeningTestModel.testName = null;
        screeningTestModel.selectedTestId = null;
        screeningTestModel.isTestSelected = false;

        this.setState(screeningTestModel);
    }

    private handleFollowupStatusChange(medicalRequestId: string, medicalRequestType: MedicalRequestType, statusLookupId: string): void {
        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.UpdateFollowupStatus,
            {
                medicalRequestId,
                medicalRequestType,
                statusLookupId
            }
        );

        const followup = this.state.followups.firstOrDefault(x => x.Id === medicalRequestId && x.MedicalRequestType === medicalRequestType);
        if (followup) {
            followup.StatusLookupId = statusLookupId;
            this.mutateState(s => {
                s.followups = s.followups.map(x => (x.Id === followup.Id && x.MedicalRequestType === followup.MedicalRequestType) ? followup : x);
                if (MedicalRequestStatus.isInPendingStatus(statusLookupId))
                    this.changeStatus(s, ScreeningTestStatus.Pending.LookupID);
            });
        }
    }

    private handleCommentAdded(medicalRequestId: string, medicalRequestType: MedicalRequestType, comment: string): void {
        if (!comment)
            return;

        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.InsertMedicalRequestComment,
            {
                requestId: medicalRequestId,
                requestType: medicalRequestType,
                comment
            }
        );

        const followup = this.state.followups.firstOrDefault(x => x.Id === medicalRequestId && x.MedicalRequestType === medicalRequestType);
        if (followup) {
            followup.CommentsCount = (followup.CommentsCount || 0) + 1;
            followup.LatestComment = comment;
            this.mutateState(s => {
                s.followups = s.followups.map(x => (x.Id === followup.Id && x.MedicalRequestType === followup.MedicalRequestType) ? followup : x);
            });
        }
    }

    private isInvalid(valueToCheck: any): boolean {
        return (this.state.selectedTestId != null || this.state.testName != null) && !valueToCheck;
    }

    private toSearchableWithLatestScreenings(screeningTestDefinitions: ScreeningTestDefinition[]): SearchableItemModelSection<ScreeningTestDefinition>[] {
        const toSearchableList: SearchableItemModelSection<ScreeningTestDefinition>[] = [];
        const latestMedicalItems = MedicalItemScreeningTest.getLatestScreeningsToDisplay(this.props.PatientMedicalSummaryModel);

        screeningTestDefinitions.forEach(screeningTestDefinition => {
            const searchableItem = new SearchableItemModelSection<ScreeningTestDefinition>(this.newEntrySection, screeningTestDefinition.getTranslatedTestDefinitionName(), screeningTestDefinition.Id.toString(), screeningTestDefinition);
            if (!latestMedicalItems.any(p => p.getTestDefinition()?.Name == screeningTestDefinition.Name)) {
                toSearchableList.push(searchableItem);
            }
        });

        latestMedicalItems.forEach(medicalItem => {
            const model = new ScreeningTestDefinition();
            model.Name = medicalItem.getScreeningTestTitle();
            const searchableItem = new SearchableItemModelSection<ScreeningTestDefinition>(this.newEntrySection, model.Name, model.Name, model);
            searchableItem.Section = this.alreadyOnFileSection;
            toSearchableList.push(searchableItem);
        });

        return toSearchableList;
    }

    private adjustScrollPosition(): void {
        const latestElement = document.getElementById("latestItem");
        const elementToScrollTo = latestElement ? document.getElementById("latestItem").parentElement : null;
        const mainContainerScroll = document.getElementById("autoscroll_0");

        if (elementToScrollTo && mainContainerScroll) {
            const scrollPosition = elementToScrollTo.offsetTop - mainContainerScroll.offsetTop;
            $("#autoscroll_0").animate({ scrollTop: scrollPosition }, 200);
        }
    }

    private async onMedicalRequestPrintSuccess(fileName: ExportableFile, id: string, medicalRequestType: MedicalRequestType): Promise<void> {
        if (!fileName || !id)
            return;

        PrintHelper.ShowPrintPreviewFileName((await fileName.getFetchUrlDtoAsync())?.FetchUrl);

        this.mutateState(s => {
            s.followups = s.followups.mutate(x => x.IsPrinted = true, x => x.Id === id && x.MedicalRequestType === medicalRequestType);
        });

        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.UpdateScreeningsModelAfterPrintingRequest,
            {
                requestId: id,
                requestType: medicalRequestType
            }
        );
    }

    private changeStatus(state: MedicalItemsScreeningTestViewState, statusLookupId: string): void {
        state.selectedStatusLookupId = statusLookupId;
        if (!MedicalItemScreeningTest.isCompleted(statusLookupId)) {
            state.completionDate = null;
            state.completionDateText = "";
            state.selectedInterpretationLookupId = "";
        }
    }

    private handleOnAddTimeToDueDate(newDate: Date): void {
        this.mutateState(s => s.screeningDueDate = s.screeningDueDate.updateDueDate(newDate));
    }

    private getRequestingStaffContactFacetId(): string | undefined {
        if (sessionStore.isLockedData("ScreeningTestModel")) {
            const lockedData = sessionStore.getLockedData<{ requestingStaffContactFacetId: string }>("ScreeningTestModel");
            return lockedData.requestingStaffContactFacetId;
        }
        else {
            return this.props.PatientModel?.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee.ContactFacetId
                ?? this.props.PatientModel?.GetAttendingDoctor()?.ContactFacetId
                ?? sessionStore.getCurrentEmployee().ContactFacetId;
        }
    }

    private onLockIconClick(): void {
        if (!this.state.requestingStaffContactFacetId)
            return;

        if (sessionStore.isLockedData("ScreeningTestModel")) {
            sessionStore.clearLockedData("ScreeningTestModel");
        }
        else {
            sessionStore.setLockData("ScreeningTestModel", { requestingStaffContactFacetId: this.state.requestingStaffContactFacetId });
        }

        this.forceUpdate();
    }

    private onClearButtonClick(): void {
        this.mutateState(s => s.requestingStaffContactFacetId = "");
        sessionStore.clearLockedData("ScreeningTestModel");
    }
}

export { MedicalItemsScreeningTestView, MedicalItemsScreeningTestViewState };
